import {
  ArdoqId,
  Entity,
  HTTPStatusCode,
  RenameRequest,
  UnpersistedEntity,
  WithoutMetadata,
} from '@ardoq/api-types';
import {
  ArdoqError,
  getArdoqErrorMessage,
  isArdoqError,
  Result,
  toArdoqError,
} from '@ardoq/common-helpers';
import { logError } from '@ardoq/logging';
import { getErrorStatusCode } from './error';
import { ApiResponse, FetchError } from './types';

export const UNAUTHORIZED_ERROR = 'ajax-unauthorized-error';
export const IMPERSONATION_WRITE_BLOCKED_ERROR =
  'impersonation-write-blocked-error';
export const FALLBACK_ERROR_MESSAGE = `Something went wrong, and we're unable to create a new connection. Please try connecting again. Contact support if the problem continues.`;
export const ARDOQ_TRACE_ID_HEADER = 'x-ardoq-trace-id';
export const CSRF_HEADER_NAME = 'X-CSRF-Token';

const defaultValueIfError = <T>(response: Result<T>, defaultValue: T) => {
  return isArdoqError(response) ? defaultValue : response;
};

const normalize = (urlString: string) => {
  const url = new URL(urlString, window.origin);
  const parts = url.pathname.split('/');
  // replace ids with $id
  return parts.map(part => part.replace(/[a-f|\d]{24}/g, '$id')).join('/');
};

export enum Method {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  DELETE = 'DELETE',
  PATCH = 'PATCH',
}

export const headers = {
  'Content-Type': 'application/json',
};
const toBody = (body: any) => (body ? { body: JSON.stringify(body) } : {});

const CSRF_COOKIE_NAME = '__Host-ardoq-csrf';

const MUTATING_METHODS: ReadonlySet<Method> = new Set([
  Method.POST,
  Method.PUT,
  Method.DELETE,
  Method.PATCH,
]);

export const getCsrfToken = (): string | undefined =>
  document.cookie
    .split('; ')
    .find(row => row.startsWith(`${CSRF_COOKIE_NAME}=`))
    ?.slice(CSRF_COOKIE_NAME.length + 1);

export const toRequestOptions = (
  method: Method,
  body: any,
  signal?: AbortSignal
): RequestInit => {
  const token = MUTATING_METHODS.has(method) ? getCsrfToken() : undefined;
  return {
    method,
    headers: token ? { ...headers, [CSRF_HEADER_NAME]: token } : headers,
    signal,
    ...toBody(body),
  };
};

export const toApiResponse = async <T>(response: Response): ApiResponse<T> => {
  if (!response.ok) {
    return toResponseError(response);
  }
  return response.json();
};

const toBlobResponse = async (response: Response): ApiResponse<Blob> => {
  if (!response.ok) {
    return toResponseError(response);
  }
  return response.blob();
};

export type BlobWithFileName = { blob: Blob; fileName: string };

const CONTENT_DISPOSITION_FILENAME = /filename="?([^";]+)"?/i;

const getFileNameFromContentDisposition = (header: string | null): string => {
  const match = header?.match(CONTENT_DISPOSITION_FILENAME);
  return match ? match[1] : 'export';
};

const toBlobWithFileNameResponse = async (
  response: Response
): ApiResponse<BlobWithFileName> => {
  if (!response.ok) {
    return toResponseError(response);
  }
  const blob = await response.blob();
  const fileName = getFileNameFromContentDisposition(
    response.headers.get('Content-Disposition')
  );
  return { blob, fileName };
};

type ErrorResponseBody = {
  message?: string;
  errors?: string;
  error?: string;
  code?: string;
  details?: Record<string, unknown>;
};

const getResponseErrorBody = async (
  response: Response
): Promise<ErrorResponseBody | null> => {
  try {
    const json: unknown = await response.json();
    return json && typeof json === 'object' && !Array.isArray(json)
      ? (json as ErrorResponseBody)
      : null;
  } catch {
    return null;
  }
};

const getErrorMessageFromBody = (
  body: ErrorResponseBody | null
): string | null =>
  body ? (body.message ?? body.errors ?? body.error ?? null) : null;

const getErrorInformation = async (
  response: Response
): Promise<{
  developerMessage: string;
  userMessage: string;
  traceId: string | undefined;
  errorCode: string | undefined;
  errorDetails: Record<string, unknown> | undefined;
}> => {
  const body = await getResponseErrorBody(response);
  const responseErrorMessage = getErrorMessageFromBody(body);
  const developerMessage = `${normalize(response.url)} ${response.status} - ${
    responseErrorMessage ?? response.statusText
  }`;
  const userMessage = responseErrorMessage ?? FALLBACK_ERROR_MESSAGE;
  const traceId = response.headers.get(ARDOQ_TRACE_ID_HEADER) ?? undefined;
  const errorCode = typeof body?.code === 'string' ? body.code : undefined;
  const errorDetails =
    body?.details &&
    typeof body.details === 'object' &&
    !Array.isArray(body.details)
      ? (body.details as Record<string, unknown>)
      : undefined;

  return {
    developerMessage,
    userMessage,
    traceId,
    errorCode,
    errorDetails,
  };
};

/**
 * Returns an ArdoqError based on a Response
 *
 * Developer message is constructed from the URL, status code and either the error message
 * as provided by the JSON body of the response or the status text of the response.
 *
 * User message is constructed from the error message as provided by the JSON body of the response,
 * or a generic fallback message.
 */
export const toResponseError = async (
  response: Response
): Promise<ArdoqError> => {
  if (response.ok) {
    throw new Error(
      `Received a successful response: ${response.status}, did you forget to check response.ok?`
    );
  }

  maybeEmitUnauthorizedEvent(response.status);

  const { developerMessage, userMessage, traceId, errorCode, errorDetails } =
    await getErrorInformation(response);

  maybeEmitImpersonationWriteBlockedEvent(response.status, errorCode);

  return toArdoqError({
    error: new FetchError(developerMessage, { response }),
    userMessage,
    traceId,
    errorCode,
    errorDetails,
  });
};

const toReturnWithResponse = async <T>(
  response: Response,
  data: T
): ApiResponse<T> => {
  if (!response.ok) {
    return toResponseError(response);
  }
  return data;
};

// https://ardoqcom.atlassian.net/browse/ARD-24023
// This will be removed eventually once the integration API stops sending 401 errors.
export const fetchIntegrationJson = <T>(request: Request): ApiResponse<T> =>
  window
    .fetch(request)
    .then(async response => {
      if (!response.ok) {
        const {
          developerMessage,
          userMessage,
          traceId,
          errorCode,
          errorDetails,
        } = await getErrorInformation(response);

        return toArdoqError({
          error: new FetchError(developerMessage, { response }),
          userMessage,
          traceId,
          errorCode,
          errorDetails,
        });
      }

      return response.json();
    })
    .catch(error => handleNetworkError(request, error));

export const fetchJson = <T>(request: Request): ApiResponse<T> =>
  window
    .fetch(request)
    .then(response => toApiResponse<T>(response))
    .catch(error => handleNetworkError(request, error));

export const fetchVoid = (request: Request): ApiResponse<void> =>
  fetchAndRespondWith(request, undefined);

export const fetchAndRespondWith = <T>(
  request: Request,
  data: T
): ApiResponse<T> =>
  window
    .fetch(request)
    .then(response => toReturnWithResponse(response, data))
    .catch(error => handleNetworkError(request, error));

export const fetchVoidAndRespondWithId = (
  request: Request,
  id: ArdoqId
): ApiResponse<ArdoqId> => fetchAndRespondWith(request, id);

export const fetchBlob = (request: Request): ApiResponse<Blob> =>
  window
    .fetch(request)
    .then(response => toBlobResponse(response))
    .catch(error => handleNetworkError(request, error));

export const fetchBlobWithFileName = (
  request: Request
): ApiResponse<BlobWithFileName> =>
  window
    .fetch(request)
    .then(response => toBlobWithFileNameResponse(response))
    .catch(error => handleNetworkError(request, error));

export const handleNetworkError = (
  request: Request,
  error: unknown
): ArdoqError => {
  const isAborted = Boolean(request.signal?.aborted);
  if (isArdoqError(error)) {
    return error;
  }

  return toArdoqError({
    error: new Error(`Failed to fetch ${normalize(request.url)}`, {
      cause: error,
    }),
    isAborted,
  });
};

export const fetchEntity =
  <T extends Entity>(pathFragment: string) =>
  (id: ArdoqId): ApiResponse<T> => {
    const request = new Request(`/api/${pathFragment}/${id}`, { headers });
    return fetchJson<T>(request);
  };

export const fetchEntities =
  <T extends Entity>(pathFragment: string) =>
  (): ApiResponse<T[]> => {
    const request = new Request(`/api/${pathFragment}`, { headers });
    return fetchJson<T[]>(request);
  };

export const deleteEntity =
  (pathFragment: string) =>
  async (id: ArdoqId): ApiResponse<ArdoqId> => {
    const request = new Request(
      `/api/${pathFragment}/${id}`,
      toRequestOptions(Method.DELETE, {})
    );
    const response = await fetchVoid(request);
    if (isArdoqError(response)) {
      return response;
    }
    return id;
  };

export const createEntity =
  <T, CreateableT = T>(pathFragment: string) =>
  (entity: UnpersistedEntity<CreateableT>): ApiResponse<T> => {
    const request = new Request(
      `/api/${pathFragment}`,
      toRequestOptions(Method.POST, entity)
    );
    return fetchJson<T>(request);
  };

export const updateEntity =
  <T extends Entity>(pathFragment: string) =>
  (entity: WithoutMetadata<T>): ApiResponse<T> => {
    const request = new Request(
      `/api/${pathFragment}/${entity._id}`,
      toRequestOptions(Method.PUT, entity) // no bueno
    );
    return fetchJson<T>(request);
  };

export const renameEntity =
  <T extends Entity>(pathFragment: string) =>
  async ({ name, _id, _version }: RenameRequest): ApiResponse<T> => {
    const request = new Request(
      `/api/${pathFragment}/${_id}/rename`,
      toRequestOptions(Method.PATCH, { name, _version })
    );
    return fetchJson<T>(request);
  };

export const crudApi = <T extends Entity, CreatableT = T>(
  pathFragment: string
) => ({
  create: createEntity<T, CreatableT>(pathFragment),
  update: updateEntity<T>(pathFragment),
  delete: deleteEntity(pathFragment),
  fetch: fetchEntity<T>(pathFragment),
  fetchAll: fetchEntities<T>(pathFragment),
});

const shouldLog = (status: HTTPStatusCode) =>
  !(
    status === HTTPStatusCode.UNAUTHORIZED ||
    status === HTTPStatusCode.FORBIDDEN
  );

const logErrorIfNeeded = <T>(response: Result<T>): T | null => {
  if (isArdoqError(response)) {
    const statusCode = getErrorStatusCode(response);

    if (shouldLog(statusCode)) {
      logError(response);
    }

    return null;
  }

  return response;
};

const logErrorIfNeededAndReturnMessage = (
  error: ArdoqError,
  defaultMessage: string
): string => {
  const message = getArdoqErrorMessage(error, defaultMessage);
  const statusCode = getErrorStatusCode(error);

  if (shouldLog(statusCode)) {
    logError(error, message);
  }

  return message;
};

export const maybeEmitUnauthorizedEvent = (status: number) => {
  if (status === HTTPStatusCode.UNAUTHORIZED) {
    document.dispatchEvent(new CustomEvent(UNAUTHORIZED_ERROR));
  }
};

const IMPERSONATION_WRITE_BLOCKED_CODE = 'impersonation.writeBlocked';

const maybeEmitImpersonationWriteBlockedEvent = (
  status: number,
  errorCode: string | undefined
) => {
  if (
    status === HTTPStatusCode.FORBIDDEN &&
    errorCode === IMPERSONATION_WRITE_BLOCKED_CODE
  ) {
    document.dispatchEvent(new CustomEvent(IMPERSONATION_WRITE_BLOCKED_ERROR));
  }
};

const isHttpError = (statusCode: HTTPStatusCode, error: unknown) =>
  getErrorStatusCode(error) === statusCode;

const isServiceUnavailable = (error: unknown) =>
  isHttpError(HTTPStatusCode.SERVICE_UNAVAILABLE, error);

const isBadRequest = (error: unknown) =>
  isHttpError(HTTPStatusCode.BAD_REQUEST, error);

const isUnauthorized = (error: unknown) =>
  isHttpError(HTTPStatusCode.UNAUTHORIZED, error);

const isConflict = (error: unknown) =>
  isHttpError(HTTPStatusCode.CONFLICT, error);

const isUnsupportedMediaType = (error: unknown) =>
  isHttpError(HTTPStatusCode.UNSUPPORTED_MEDIA_TYPE, error);

const isForbidden = (error: unknown) =>
  isHttpError(HTTPStatusCode.FORBIDDEN, error);

const isGatewayTimeout = (error: unknown) =>
  isHttpError(HTTPStatusCode.GATEWAY_TIMEOUT, error);

const isNotFound = (error: unknown) =>
  isHttpError(HTTPStatusCode.NOT_FOUND, error);

const isCreated = (error: unknown) =>
  isHttpError(HTTPStatusCode.CREATED, error);

const isUnprocessable = (error: unknown) =>
  isHttpError(HTTPStatusCode.UNPROCESSABLE_ENTITY, error);

export const api = {
  isConflict,
  isBadRequest,
  isGatewayTimeout,
  isUnsupportedMediaType,
  isUnauthorized,
  isForbidden,
  isNotFound,
  isCreated,
  isUnprocessable,
  isServiceUnavailable,
  defaultValueIfError,
  logErrorIfNeeded,
  logErrorIfNeededAndReturnMessage,
  shouldLog,
  toApiResponse,
};
